import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    Card,
    Button,
    Breadcrumb,
    Table,
    Pagination,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllShifts, createShift, updateShift } from "../../../../api/onboarddeboard";

const DAYS = [
    { key: "mon", label: "Monday" },
    { key: "tue", label: "Tuesday" },
    { key: "wed", label: "Wednesday" },
    { key: "thu", label: "Thursday" },
    { key: "fri", label: "Friday" },
    { key: "sat", label: "Saturday" },
    { key: "sun", label: "Sunday" },
];

const ITEMS_PER_PAGE = 20;

const defaultDayState = () =>
    DAYS.reduce((acc, d) => {
        acc[`${d.key}In`] = "";
        acc[`${d.key}Out`] = "";
        acc[`${d.key}WeekOff`] = false;
        return acc;
    }, {});

const Shifts = () => {
    const token = localStorage.getItem("token");

    const [shiftName, setShiftName] = useState("");
    const [waveOffTime, setWaveOffTime] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [dayTimings, setDayTimings] = useState(defaultDayState());
    const [errors, setErrors] = useState({});
    const [showMinutePicker, setShowMinutePicker] = useState(false);
    const minutePickerRef = useRef(null);

    const [editingId, setEditingId] = useState(null);
    const [originalValues, setOriginalValues] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOption, setSortOption] = useState("latest");
    const [filterActive, setFilterActive] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const fetchShifts = useCallback(async () => {
        try {
            const response = await getAllShifts(token);
            const sorted = (response.data || []).sort((a, b) => b.id - a.id);
            setShifts(sorted);
        } catch (error) {
            toast.error("Failed to fetch shifts.");
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchShifts();
    }, [token, fetchShifts]);

    const handleDayChange = (key, field, value) => {
        setDayTimings((prev) => {
            const updated = { ...prev, [`${key}${field}`]: value };

            const inTime = field === "In" ? value : updated[`${key}In`];
            const outTime = field === "Out" ? value : updated[`${key}Out`];

            if (inTime && outTime && inTime === outTime) {
                setErrors((prevErr) => ({
                    ...prevErr,
                    [`${key}Out`]: `${key.charAt(0).toUpperCase() + key.slice(1)} OUT time cannot be same as IN time`,
                }));
            } else {
                setErrors((prevErr) => ({
                    ...prevErr,
                    [`${key}Out`]: "",
                    [`${key}In`]: "",
                }));
            }

            return updated;
        });
    };

    const handleWeekOffChange = (key, checked) => {
        setDayTimings((prev) => ({
            ...prev,
            [`${key}WeekOff`]: checked,
            [`${key}In`]: checked ? "" : prev[`${key}In`],
            [`${key}Out`]: checked ? "" : prev[`${key}Out`],
        }));
        setErrors((prev) => ({
            ...prev,
            [`${key}In`]: "",
            [`${key}Out`]: "",
        }));
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (minutePickerRef.current && !minutePickerRef.current.contains(e.target)) {
                setShowMinutePicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

     useEffect(() => {
        if (!shiftName.trim() || shifts.length === 0) return;
        const duplicate = shifts.some(
            (s) =>
                s.shiftName.toLowerCase() === shiftName.trim().toLowerCase() &&
                (!editingId || s.id !== editingId)
        );
        setErrors((prev) => ({
            ...prev,
            shiftName: duplicate
                ? "A Shift with this name already exists."
                : prev.shiftName === "A Shift with this name already exists."
                ? ""
                : prev.shiftName,
        }));
    }, [shiftName, shifts, editingId]);

    
  const validate = () => {
    const newErrors = {};

    if (/^\s+$/.test(shiftName)) {
      newErrors.shiftName = "Shift Name cannot be only spaces.";
    } else if (!shiftName.trim()) {
      newErrors.shiftName = "Shift Name is required";
    } else if (/^\d+$/.test(shiftName.trim())) {
      newErrors.shiftName = "Shift Name cannot be only numeric.";
    } else if (/^[^a-zA-Z0-9]+$/.test(shiftName.trim())) {
      newErrors.shiftName = "Shift Name cannot be only special characters.";
    } else if (/^[^a-zA-Z]*$/.test(shiftName.trim()) && /[0-9]/.test(shiftName.trim()) && /[^a-zA-Z0-9]/.test(shiftName.trim())) {
      newErrors.shiftName = "Shift Name cannot be only numbers and special characters.";
    } else if (shiftName.trim().length > 50) {
      newErrors.shiftName = "Shift Name must not exceed 50 characters";
    } else {
      const duplicate = shifts.some(
        (s) =>
          s.shiftName.toLowerCase() === shiftName.trim().toLowerCase() &&
          (!editingId || s.id !== editingId)
      );
      if (duplicate) {
        newErrors.shiftName = "A Shift with this name already exists.";
      }
    }

        if (waveOffTime === "" || waveOffTime === null || waveOffTime === undefined) {
            newErrors.waveOffTime = "Wave Off Time is required";
        }
        DAYS.forEach((d) => {
            const isWeekOff = dayTimings[`${d.key}WeekOff`];
            if (!isWeekOff) {
                if (!dayTimings[`${d.key}In`]) {
                    newErrors[`${d.key}In`] = `${d.label} IN time is required`;
                }
                if (!dayTimings[`${d.key}Out`]) {
                    newErrors[`${d.key}Out`] = `${d.label} OUT time is required`;
                }
                if (
                    dayTimings[`${d.key}In`] &&
                    dayTimings[`${d.key}Out`] &&
                    dayTimings[`${d.key}In`] === dayTimings[`${d.key}Out`]
                ) {
                    newErrors[`${d.key}Out`] = `${d.label} OUT time cannot be same as IN time`;
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

   const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (editingId && originalValues) {
        const noChanges =
            shiftName.trim() === originalValues.shiftName.trim() &&
            String(waveOffTime) === String(originalValues.waveOffTime) &&
            isActive === originalValues.isActive &&
            JSON.stringify(dayTimings) === JSON.stringify(originalValues.dayTimings);

        if (noChanges) {
            toast.warning("No changes detected.");
            return;
        }
    }

    const payload = {
            shiftName: shiftName.trim(),
            waveOffTime: Number(waveOffTime),
            isActive,
            ...DAYS.reduce((acc, d) => {
                acc[`${d.key}In`] = dayTimings[`${d.key}WeekOff`] ? null : dayTimings[`${d.key}In`] || null;
                acc[`${d.key}Out`] = dayTimings[`${d.key}WeekOff`] ? null : dayTimings[`${d.key}Out`] || null;
                acc[`${d.key}WeekOff`] = dayTimings[`${d.key}WeekOff`];
                return acc;
            }, {}),
        };

        try {
            if (editingId) {
                await updateShift(editingId, payload, token);
                await fetchShifts();
                resetForm();
                toast.success("Shift updated successfully");
            } else {
                await createShift(payload, token);
                await fetchShifts();
                resetForm();
                toast.success("Shift created successfully");
            }
        } catch (error) {
            const data = error?.response?.data;
            const message = typeof data === "string"
            (typeof data === "string" && data.trim())
                ? data
                : data?.message || data?.error || "Failed to save shift";
            toast.error(message);
        }
    };

   const handleEdit = (shift) => {
    setEditingId(shift.id);
    setShiftName(shift.shiftName);
    setWaveOffTime(shift.waveOffTime ?? "");
    setIsActive(shift.isActive);

    const timings = {};
    DAYS.forEach((d) => {
        timings[`${d.key}In`] = shift[`${d.key}In`] || "";
        timings[`${d.key}Out`] = shift[`${d.key}Out`] || "";
        timings[`${d.key}WeekOff`] = shift[`${d.key}WeekOff`] || false;
    });
    setDayTimings(timings);
    setErrors({});
    setOriginalValues({
        shiftName: shift.shiftName,
        waveOffTime: shift.waveOffTime ?? "",
        isActive: shift.isActive,
        dayTimings: timings,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
};

   const resetForm = () => {
    setShiftName("");
    setWaveOffTime("");
    setIsActive(true);
    setDayTimings(defaultDayState());
    setEditingId(null);
    setErrors({});
    setOriginalValues(null);
};
    const exportToCSV = () => {
        if (shifts.length === 0) {
            toast.error("No data available to export");
            return;
        }

        const headers = ["#", "Shift Name", "Wave Off (min)", "Working Days", "Week Off Days", "Status"];

        const rows = shifts.map((shift, index) => {
            const workingDays = DAYS.filter((d) => !shift[`${d.key}WeekOff`]).map((d) => d.label).join(" | ");
            const weekOffDays = DAYS.filter((d) => shift[`${d.key}WeekOff`]).map((d) => d.label).join(" | ");
            return [
                index + 1,
                shift.shiftName,
                shift.waveOffTime,
                workingDays || "-",
                weekOffDays || "-",
                shift.isActive ? "Active" : "Inactive",
            ];
        });

        const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "shifts.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const displayedShifts = shifts
        .filter((s) => {
            const matchesSearch = s.shiftName.toLowerCase().startsWith(searchTerm.toLowerCase().trim());
            const matchesStatus = filterActive === ""
                ? true
                : s.isActive === (filterActive === "true");
            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            if (sortOption === "nameAZ") return a.shiftName.localeCompare(b.shiftName);
            return b.id - a.id;
        });

    const totalPages = Math.ceil(displayedShifts.length / ITEMS_PER_PAGE);
    const paginated = displayedShifts.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const getWorkingDays = (shift) =>
        DAYS.filter((d) => !shift[`${d.key}WeekOff`]).map((d) => d.label.slice(0, 3)).join(", ");

    const getWeekOffDays = (shift) =>
        DAYS.filter((d) => shift[`${d.key}WeekOff`]).map((d) => d.label.slice(0, 3)).join(", ") || "-";

    return (
        <Container fluid className="p-4">
            <style>{`
                .leave-toggle .form-check-input:checked {
                    background-color: #2E5CB8 !important;
                    border-color: #2E5CB8 !important;
                }
                .leave-toggle .form-check-input:focus {
                    border-color: #2E5CB8 !important;
                    box-shadow: 0 0 0 0.1rem rgba(46, 92, 184, 0.35) !important;
                }
                .lc-breadcrumb a {
                    text-decoration: none !important;
                    color: #2E5CB8 !important;
                }
                .lc-breadcrumb .breadcrumb-item.active {
                    color: #6c757d !important;
                }
                .pagination .page-item.active .page-link {
                    background-color: #2E5CB8 !important;
                    border-color: #2E5CB8 !important;
                }
            `}</style>
<h4 className="page-main-heading" style={{ marginBottom: 12 }}>
                Shifts
            </h4>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
                <Breadcrumb.Item href="#">Shift & Authorities</Breadcrumb.Item>
                <Breadcrumb.Item active>Shift</Breadcrumb.Item>
            </Breadcrumb>

            <Row className="mb-4">
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Shift Name<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Shift Name"
                                                    value={shiftName}
                                                    maxLength={50}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setShiftName(value);
                                                        const trimmedValue = value.trim();

                                                        if (/^\s+$/.test(value)) {
                                                            setErrors((prev) => ({
                                                                ...prev,
                                                                shiftName: "Shift Name cannot be only spaces.",
                                                            }));
                                                            return;
                                                        }

                                                        if (/^\d+$/.test(trimmedValue)) {
                                                            setErrors((prev) => ({
                                                                ...prev,
                                                                shiftName: "Shift Name cannot be only numeric.",
                                                            }));
                                                            return;
                                                        }

                                                        if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
                                                            setErrors((prev) => ({
                                                                ...prev,
                                                                shiftName: "Shift Name cannot be only special characters.",
                                                            }));
                                                            return;
                                                        }

                                                        if (/^[^a-zA-Z]*$/.test(trimmedValue) && /[0-9]/.test(trimmedValue) && /[^a-zA-Z0-9]/.test(trimmedValue)) {
                                                            setErrors((prev) => ({
                                                                ...prev,
                                                                shiftName: "Shift Name cannot be only numbers and special characters.",
                                                            }));
                                                            return;
                                                        }

                                                        setErrors((prev) => ({ ...prev, shiftName: "" }));
                                                    }}
                                                />
                                                {errors.shiftName && (
                                                    <p style={{ color: "red", fontSize: "13px" }}>{errors.shiftName}</p>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Wave Off Time (min)<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <div ref={minutePickerRef} style={{ position: "relative" }}>
                                                    <div
                                                        onClick={() => setShowMinutePicker((prev) => !prev)}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            padding: "8px 12px",
                                                            border: "1px solid #dee2e6",
                                                            borderRadius: "6px",
                                                            cursor: "pointer",
                                                            backgroundColor: "#fff",
                                                            userSelect: "none",
                                                            minHeight: "38px",
                                                        }}
                                                    >
                                                        <span style={{
                                                            fontSize: "16px",
                                                            fontWeight: "400",
                                                            color: waveOffTime !== "" ? "#212529" : "#adb5bd",
                                                        }}>
                                                            {waveOffTime !== "" ? String(waveOffTime).padStart(2, "0") : "--"}
                                                        </span>
                                                        <span style={{ fontSize: "12px", color: "#6c757d" }}>
                                                            <i className={`bi bi-chevron-${showMinutePicker ? "up" : "down"}`} />
                                                        </span>
                                                    </div>

                                                    {showMinutePicker && (
                                                        <div style={{
                                                            position: "absolute",
                                                            top: "110%",
                                                            left: 0,
                                                            zIndex: 9999,
                                                            background: "#fff",
                                                            border: "1px solid #dee2e6",
                                                            borderRadius: "8px",
                                                            width: "130px",
                                                            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                                                            overflow: "hidden",
                                                        }}>
                                                            <div style={{ maxHeight: "200px", overflowY: "auto", scrollbarWidth: "thin" }}>
                                                                <div
                                                                    onClick={() => {
                                                                        setWaveOffTime("");
                                                                        setShowMinutePicker(false);
                                                                    }}
                                                                    style={{
                                                                        padding: "8px 0",
                                                                        textAlign: "center",
                                                                        cursor: "pointer",
                                                                        fontSize: "15px",
                                                                        fontWeight: "400",
                                                                        color: "#6c757d",
                                                                        borderBottom: "1px solid #dee2e6",
                                                                    }}
                                                                >
                                                                    Clear
                                                                </div>

                                                                {Array.from({ length: 61 }, (_, i) => (
                                                                    <div
                                                                        key={i}
                                                                        onClick={() => {
                                                                            setWaveOffTime(i);
                                                                            setShowMinutePicker(false);
                                                                            setErrors((prev) => ({ ...prev, waveOffTime: "" }));
                                                                        }}
                                                                        style={{
                                                                            padding: "8px 0",
                                                                            textAlign: "center",
                                                                            cursor: "pointer",
                                                                            fontSize: "15px",
                                                                            fontWeight: waveOffTime === i ? "700" : "400",
                                                                            backgroundColor: waveOffTime === i ? "#2E5CB8" : "transparent",
                                                                            color: waveOffTime === i ? "white" : "#212529",
                                                                            transition: "background 0.15s",
                                                                        }}
                                                                    >
                                                                        {String(i).padStart(2, "0")}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.waveOffTime && (
                                                    <p style={{ color: "red", fontSize: "13px" }}>{errors.waveOffTime}</p>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Status
                                            </Form.Label>
                                            <Col sm={8} className="d-flex align-items-center gap-2">
                                                <div className="leave-toggle">
                                                    <Form.Check
                                                        type="switch"
                                                        id="status-switch"
                                                        checked={isActive}
                                                        onChange={(e) => setIsActive(e.target.checked)}
                                                    />
                                                </div>
                                                <span style={{ fontSize: "14px", color: "#6c757d" }}>if Active</span>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col>
                                        <Table bordered responsive>
                                            <thead>
                                                <tr>
                                                    <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "130px" }}>Day</th>
                                                    <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>IN Time<span className="text-warning">*</span></th>
                                                    <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>OUT Time<span className="text-warning">*</span></th>
                                                    <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "120px" }}>Week Off</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {DAYS.map((d) => {
                                                    const isWeekOff = dayTimings[`${d.key}WeekOff`];
                                                    return (
                                                        <tr key={d.key}>
                                                            <td><strong>{d.label}</strong></td>
                                                            <td>
                                                                <div
                                                                    style={{ cursor: isWeekOff ? "not-allowed" : "pointer" }}
                                                                    onClick={() => {
                                                                        if (!isWeekOff) {
                                                                            document.getElementById(`${d.key}In`).showPicker();
                                                                        }
                                                                    }}
                                                                >
                                                                    <Form.Control
                                                                        id={`${d.key}In`}
                                                                        type="time"
                                                                        value={dayTimings[`${d.key}In`]}
                                                                        disabled={isWeekOff}
                                                                        onChange={(e) => handleDayChange(d.key, "In", e.target.value)}
                                                                        style={{ cursor: isWeekOff ? "not-allowed" : "pointer" }}
                                                                    />
                                                                </div>
                                                                {errors[`${d.key}In`] && (
                                                                    <small style={{ color: "red" }}>{errors[`${d.key}In`]}</small>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div
                                                                    style={{ cursor: isWeekOff ? "not-allowed" : "pointer" }}
                                                                    onClick={() => {
                                                                        if (!isWeekOff) {
                                                                            document.getElementById(`${d.key}Out`).showPicker();
                                                                        }
                                                                    }}
                                                                >
                                                                    <Form.Control
                                                                        id={`${d.key}Out`}
                                                                        type="time"
                                                                        value={dayTimings[`${d.key}Out`]}
                                                                        disabled={isWeekOff}
                                                                        onChange={(e) => handleDayChange(d.key, "Out", e.target.value)}
                                                                        style={{ cursor: isWeekOff ? "not-allowed" : "pointer" }}
                                                                    />
                                                                </div>
                                                                {errors[`${d.key}Out`] && (
                                                                    <small style={{ color: "red" }}>{errors[`${d.key}Out`]}</small>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="leave-toggle">
                                                                    <Form.Check
                                                                        type="switch"
                                                                        id={`${d.key}-weekoff`}
                                                                        checked={isWeekOff}
                                                                        onChange={(e) => handleWeekOffChange(d.key, e.target.checked)}
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>

                                <Row className="align-items-center">
                                    <Col className="mt-2 d-flex align-items-center justify-content-center">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}
                                        >
                                            {editingId ? "Update" : "Submit"}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            style={{ marginLeft: "10px" }}
                                            onClick={resetForm}
                                        >
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            <Row className="mb-3 d-flex justify-content-end align-items-center g-2">
                                <Col md={3}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by Shift Name..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Select
                                        value={sortOption}
                                        onChange={(e) => {
                                            setSortOption(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="latest">Latest First</option>
                                        <option value="nameAZ">Name A→Z</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Select
                                        value={filterActive}
                                        onChange={(e) => {
                                            setFilterActive(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </Form.Select>
                                </Col>
                                <Col md="auto">
                                    <i
                                        className="bi bi-download"
                                        title="Export CSV"
                                        style={{ fontSize: "1.3rem", cursor: "pointer", color: "#2E5CB8" }}
                                        onClick={exportToCSV}
                                    />
                                </Col>
                            </Row>

                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Sr.No</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Shift Name</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Wave Off (min)</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Working Days</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Week Off Days</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Status</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length > 0 ? (
                                        paginated.map((shift, index) => (
                                            <tr key={shift.id}>
                                                <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                                <td>{shift.shiftName}</td>
                                                <td>{shift.waveOffTime}</td>
                                                <td>{getWorkingDays(shift)}</td>
                                                <td>{getWeekOffDays(shift)}</td>
                                                <td>{shift.isActive ? "Active" : "Inactive"}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={() => handleEdit(shift)}
                                                        type="button"
                                                        style={{ marginBottom: "5px", padding: "5px 10px" }}
                                                    >
                                                        <i className="bi bi-pencil-fill me-2" />Edit
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center">No shifts found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>

                            <div className="d-flex justify-content-end">
                                <Pagination>
                                    <Pagination.Prev
                                        onClick={() => setCurrentPage((p) => p - 1)}
                                        disabled={currentPage === 1}
                                    />
                                    {[...Array(totalPages).keys()].map((number) => (
                                        <Pagination.Item
                                            key={number + 1}
                                            active={number + 1 === currentPage}
                                            onClick={() => setCurrentPage(number + 1)}
                                        >
                                            {number + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        onClick={() => setCurrentPage((p) => p + 1)}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                    />
                                </Pagination>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Shifts;
